/**
 * Date utilities for handling timezone conversions
 * 
 * Supabase stores dates in various formats. This utility ensures dates are parsed
 * correctly as CST/CDT (America/Chicago timezone) regardless of the format.
 */

/**
 * Parse a date string and ensure it's interpreted in CST/CDT timezone
 * 
 * Note: Dates in the database are correctly stored in UTC and should be parsed normally.
 * JavaScript will handle the timezone conversion when formatting for display.
 * 
 * @param dateString - Date string from Supabase (e.g., "2025-02-03T09:00:00+00:00")
 * @returns Date object properly parsed
 */
export const parseDateInCST = (dateString: string): Date => {
  if (!dateString) return new Date();
  
  // For date-only strings like "2025-02-03", parse at midnight CST
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return new Date(dateString + 'T00:00:00-06:00');
  }
  
  // If it's like "2025-02-03T09:00:00" without timezone info, treat as CST
  if (dateString.includes('T') && !dateString.includes('+') && !dateString.includes('-', 10) && !dateString.endsWith('Z')) {
    return new Date(dateString + '-06:00');
  }
  
  // Otherwise parse normally - dates with UTC markers (+00:00 or Z) are correct
  // JavaScript automatically converts UTC to local time for display
  return new Date(dateString);
};

/**
 * Format a date string in CST timezone with custom options
 * 
 * @param dateString - Date string from Supabase
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted date string
 */
export const formatDateInCST = (
  dateString: string,
  options: Intl.DateTimeFormatOptions
): string => {
  const date = parseDateInCST(dateString);
  return date.toLocaleDateString('en-US', {
    ...options,
    timeZone: 'America/Chicago'
  });
};

/**
 * Format a time string in CST timezone with custom options
 * 
 * @param dateString - Date string from Supabase
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted time string
 */
export const formatTimeInCST = (
  dateString: string,
  options: Intl.DateTimeFormatOptions
): string => {
  const date = parseDateInCST(dateString);
  return date.toLocaleTimeString('en-US', {
    ...options,
    timeZone: 'America/Chicago'
  });
};
